/**
 * MultiSwipe组件 - 多图滑动展示组件
 * 合并PC端ThreePicSwiper和WAP端MultiSwipe2025的功能
 */

'use client';

import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type {
  MultiSwipeIndexData,
  MultiSwipeItem as MultiSwipeItemType,
  MultiSwipeProps,
  MultiSwipeTrackingParams,
} from './types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';

// 获取alt属性的辅助函数
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  return indexData?.layout_title
    ? `${indexData.layout_title} ${componentName} ${index + 1}`
    : `${componentName} ${index + 1}`;
};

// 处理换行符的工具函数
const processedMessage = (val?: string) => {
  if (val) {
    return val.replace(/<br>/g, '\n');
  } else {
    return '';
  }
};

// 统一埋点参数Hook
const useTrackingParams = (
  item: MultiSwipeItemType,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): MultiSwipeTrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: String(item.jump_type || ''),
      mkt_jump_content: String(item.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(item.sku?.shop_sku || ''),
      business_sku: String(item.sku?.business_sku || ''),
      product_sku: String(item.sku?.sku || ''),
      product_id: String(item.sku?.id || ''),
      product_name: String(item.sku?.short_name || ''),
      group_id: String(item.product_group?.id || ''),
      group_name: String(item.product_group?.name || ''),
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

// 统一埋点处理Hook
const useTrackingHandlers = (
  trackingParams: MultiSwipeTrackingParams,
  onTrack?: (params: MultiSwipeTrackingParams) => void,
  onExpTrack?: (params: MultiSwipeTrackingParams) => void,
  onValidExpTrack?: (params: MultiSwipeTrackingParams) => void,
) => {
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && onExpTrack) {
      onExpTrack(trackingParams);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (onTrack) {
      onTrack(trackingParams);
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

// 统一滑动项组件
const MultiSwipeItem = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  moduleIndex,
  onValidExpTrack,
}: {
  item: MultiSwipeItemType;
  index: number;
  indexData: MultiSwipeIndexData;
  onTrack?: (params: MultiSwipeTrackingParams) => void;
  onExpTrack?: (params: MultiSwipeTrackingParams) => void;
  moduleIndex: number;
  onValidExpTrack?: (params: MultiSwipeTrackingParams) => void;
}) => {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData?.component_id || 'multiSwipe',
    indexData?.layout_title || '',
    indexData?.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && {
        product_group: { id: String(item.product_group.id), path: item.product_group.path },
      }),
      ...(item.crowd_recommend?.id && { crowd_recommend: { id: String(item.crowd_recommend.id) } }),
    }),
    [item],
  );

  const multiSwipeItemRef = useMergedRefs(expRef, validExposureRef);
  return (
    <div ref={multiSwipeItemRef} className="relative">
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={item.title}
        className=""
      >
        <div className="relative">
          <Image
            src={item.jump_image || ''}
            alt={item.title || getAlt('MultiSwipe', index, indexData)}
            width={isPC ? 600 : 275}
            height={400}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="w-full h-auto object-cover"
            style={{
              aspectRatio: item.jump_img_ratio || 'auto',
            }}
          />

          {isPC && (
            // PC端新样式：文案在图片下方
            <div className="mt-[8px] lg:mt-[12px] 2xl:mt-[15px]">
              {item.title && (
                <h3
                  className="font-bb text-[14px] lg:text-[18px] 2xl:text-[24px] leading-[1.2] mb-[4px] 2xl:mb-[4px] whitespace-pre-line"
                  style={{ color: item.title_color || '#282828' }}
                  dangerouslySetInnerHTML={{
                    __html: item.title.replace(/\n/g, '<br>'),
                  }}
                />
              )}
              {item.des && (
                <h4
                  className="text-[12px] lg:text-[12px] 2xl:text-[14px] leading-[14px] lg:leading-[16px] 2xl:leading-[24px] line-clamp-2 whitespace-pre-line"
                  style={{ color: item.des_color || '#808080' }}
                >
                  {processedMessage(item.des)}
                </h4>
              )}
            </div>
          )}
          {!isPC && (
            // 移动端：文字显示在图片底部，垂直居中
            <div className="absolute bottom-[24px] left-1/2 transform -translate-x-1/2 w-full box-border px-[14px] flex flex-col items-center">
              {item.title && (
                <h3
                  className="font-bb text-[24px] leading-[1.2] text-center whitespace-pre-line mb-[4px]"
                  style={{ color: item.title_color || '#ffffff' }}
                  dangerouslySetInnerHTML={{
                    __html: item.title.replace(/\n/g, '<br>'),
                  }}
                />
              )}
              {item.des && (
                <h4
                  className="text-[12px] leading-[18px] text-center whitespace-pre-line line-clamp-2 w-full"
                  style={{ color: item.des_color || '#ffffff' }}
                >
                  {processedMessage(item.des)}
                </h4>
              )}
              {item.btn_text && (
                <button
                  className="mt-[12px] min-w-[145px] h-[40px] rounded-[20px] text-center leading-[40px] font-bb text-[14px]"
                  style={{
                    color: item.btn_word_color || '#ffffff',
                    backgroundColor: item.btn_bg_color || '#000000',
                  }}
                >
                  {item.btn_text}
                </button>
              )}
            </div>
          )}
        </div>
      </MagicJump>
    </div>
  );
};

export default function MultiSwipe({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: MultiSwipeProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取跳转目标列表
  const jumpTargets = useMemo(() => {
    return indexData?.jump_target || [];
  }, [indexData?.jump_target]);

  // 如果没有数据，不渲染组件
  if (!indexData || !jumpTargets.length) {
    return null;
  }

  // PC端简单配置（与Vue保持一致）
  const pcSwiperOptions = {
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    slidesPerView: 'auto' as const,
    spaceBetween: 0,
    allowTouchMove: true,
    breakpoints: {
      1024: {
        allowTouchMove: jumpTargets.length >= 4, // 数据少于4个时禁用滑动,
      },
    },
  };

  // 移动端配置（保持轮播）
  const mobileSwiperOptions = {
    modules: [Autoplay],
    slidesPerView: 1.35,
    spaceBetween: 10,
    slidesOffsetBefore: 14,
    slidesOffsetAfter: 14,
    centeredSlides: false,
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
  };

  // 根据设备类型选择配置
  const swiperOptions = isPC ? pcSwiperOptions : mobileSwiperOptions;
  const shouldShowHeading =
    Number(indexData.layout_title_status) === 1 &&
    Boolean(indexData.layout_title || indexData.layout_subtitle);

  return (
    <div
      className={`
        ${isPC ? 'my-[30px] lg:my-[50px] 2xl:my-[80px]' : 'my-[40px]'}
      `}
    >
      {/* 统一标题组件 */}
      {shouldShowHeading ? (
        <HomeSectionHeading2026
          title={indexData.layout_title}
          subtitle={indexData.layout_subtitle}
          titleColor={indexData.layout_title_color || undefined}
          subtitleColor={
            indexData.layout_subtitle_color || indexData.layout_subtitl_color || undefined
          }
          titleTag="h2"
          subtitleTag="h4"
        />
      ) : null}

      {/* 轮播容器 */}
      <div className="relative">
        <Swiper
          {...swiperOptions}
          className={isPC ? '!pl-[24px] lg:!pl-[48px] !pr-[24px] lg:!pr-[48px]' : ''}
        >
          {jumpTargets.map((item, index) => (
            <SwiperSlide
              key={item.id || index}
              className={
                isPC
                  ? `!w-[32.785%] !mr-[0.877%] last:!mr-0 min-w-[316px] lg:!min-w-[auto]` // PC端：固定宽度和间距
                  : ''
              }
            >
              <MultiSwipeItem
                item={item}
                index={index}
                indexData={indexData}
                onTrack={onTrack}
                onExpTrack={onExpTrack}
                moduleIndex={moduleIndex}
                onValidExpTrack={onValidExpTrack}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
